import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { getCheckForwardAccumulatorBody } from '@/store/forwardAccumulator/api/tradeCapture/request.ts';
import type { ForwardAccumulatorResponse } from '@/store/forwardAccumulator/api/tradeCapture/response.model.ts';
import {
  type ForwardAccumulatorTcResponse,
  mapTcResponse,
} from '@/store/forwardAccumulator/api/tradeCapture/response.ts';
import type { ForwardAccumulatorProperties } from '@/store/forwardAccumulator/state.ts';
import type { AppState } from '@/store/store.ts';

type Nullable<T> = {
  [P in keyof T]: T[P] extends string | number | boolean | undefined ? T[P] | null : T[P];
};

type NullableForwardAccumulatorProperties = Partial<Nullable<ForwardAccumulatorProperties>>;

export interface CheckForwardAccumulatorParams {
  currencyPair?: string | null;
  tileId: string;
  properties?: NullableForwardAccumulatorProperties;
}

export const checkForwardAccumulator = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<ForwardAccumulatorTcResponse, CheckForwardAccumulatorParams>({
    queryFn: async (params, api, _extraOptions, baseQuery) => {
      const state = api.getState() as AppState;
      const body = getCheckForwardAccumulatorBody(params, state);

      const tradeCapture = state.forwardAccumulator?.[params.tileId]?.tradeCapture;
      // If there was a previous Trade Capture error, we should not attempt to check the Forward Accumulator
      if (tradeCapture?.status === 'ERROR') {
        return {
          error: {
            status: 'CUSTOM_ERROR',
            error: tradeCapture.error,
            message: 'Cannot check Forward Accumulator due to previous Trade Capture error',
          },
        };
      }

      const result = await baseQuery({
        url: '/tradeCaptureBatch/Accumulator',
        method: 'POST',
        body,
        timeout: 10_000, // 10 seconds
      });

      if (result.error) {
        return { error: result.error };
      }

      const response = result.data as ForwardAccumulatorResponse;
      const tileId = params.tileId;
      const currencyPair = params.currencyPair;
      const sessionId = state.forwardAccumulator?.[params.tileId]?.tradeCapture?.sessionId;

      const transformedData = mapTcResponse(response, tileId, sessionId, currencyPair);
      return { data: transformedData };
    },
  });
};
